
import { defineComponent, inject } from 'vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import DragAndDropSvg from '../svgs/DragAndDropSvg.vue'
import draggable from 'vuedraggable'
import Dropdown from '@/components/shared/Dropdown.vue'
import TextInput from '@/components/shared/TextInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import LockingButton from '@/components/shared/LockingButton.vue'
import { InventoryTypeFieldOption } from '@/@types/responses/inventory_type/InventoryTypeFieldOption'
import { InventoryTypeField } from '@/@types/responses/inventory_type/InventoryTypeField'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import TrashCanSvg from '@/components/svgs/TrashCanSvg.vue'
import NumericInput from '@/components/shared/NumericInput.vue'
import { InventoryTypeService } from '@/services/inventoryType.service'
import { CreateInventoryTypeFieldMessage } from '@/@types/messages/CreateInventoryTypeFieldMessage'
import { UpdateInventoryTypeFieldMessage } from '@/@types/messages/UpdateInventoryTypeFieldMessage'
import { InventoryTypeFieldOptionMessage } from '@/@types/messages/InventoryTypeFieldOptionMessage'
import { InventoryTypeFieldService } from '@/services/inventoryTypeField.service'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventoryTypeFieldModal',
  components: {
    Checkbox,
    CloseSvg,
    DragAndDropSvg,
    draggable,
    Dropdown,
    LockingButton,
    NumericInput,
    TextInput,
    TrashCanSvg
  },
  props: {
    inventoryTypeField: {
      type: InventoryTypeField,
      required: true
    },
    inventoryTypeId: {
      type: Number,
      required: true
    }
  },
  emits: ['inventory-type-field-changed', 'close'],
  setup() {
    const inventoryTypeFieldService = inject(PROVIDER_CONSTANTS.inventoryTypeFieldService) as InventoryTypeFieldService
    const inventoryTypeService = inject(PROVIDER_CONSTANTS.inventoryTypeService) as InventoryTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      inventoryTypeFieldService,
      inventoryTypeService,
      toast
    }
  },
  data() {
    const typeOptions = [
      new DropdownOptionResponse(1, true, 'Manufacturer'),
      new DropdownOptionResponse(2, true, 'Supplier'),
      new DropdownOptionResponse(3, true, 'Free-Form Text'),
      new DropdownOptionResponse(4, true, 'Selection'),
      new DropdownOptionResponse(5, true, 'Yes / No'),
      new DropdownOptionResponse(6, true, 'Number'),
      new DropdownOptionResponse(7, true, 'Build Step'),
      new DropdownOptionResponse(8, true, 'Caliber')
    ]

    return {
      displayOrder: this.inventoryTypeField.displayOrder,
      inventoryTypeFieldOptions: JSON.parse(JSON.stringify(this.inventoryTypeField.inventoryTypeFieldOptions)) as InventoryTypeFieldOption[],
      isRequired: this.inventoryTypeField.isRequired,
      name: this.inventoryTypeField.name,
      saving: false,
      type: this.inventoryTypeField.type,
      typeOptions
    }
  },
  computed: {
    isFormValid(): boolean {
      let optionsChanged = false

      if (this.inventoryTypeFieldOptions.length !== this.inventoryTypeField.inventoryTypeFieldOptions.length)
        optionsChanged = true
      else {
        for (let i = 0; i < this.inventoryTypeFieldOptions.length; i++) {
          if ((this.inventoryTypeFieldOptions[i].active !== this.inventoryTypeField.inventoryTypeFieldOptions[i].active) ||
              (this.inventoryTypeFieldOptions[i].displayOrder !== this.inventoryTypeField.inventoryTypeFieldOptions[i].displayOrder) ||
              (this.inventoryTypeFieldOptions[i].value !== this.inventoryTypeField.inventoryTypeFieldOptions[i].value)) {
            optionsChanged = true
            break
          }
        }
      }

      return (this.isRequired !== this.inventoryTypeField.isRequired ||
             this.name !== this.inventoryTypeField.name ||
             this.type !== this.inventoryTypeField.type ||
             this.displayOrder !== this.inventoryTypeField.displayOrder ||
             optionsChanged) && (this.type !== undefined && this.name.trim() !== '' && this.displayOrder >= 0 && !this.inventoryTypeFieldOptions.some(i => i.value.trim() === '' || i.displayOrder < 0))
    },
    titleText(): string {
      return this.inventoryTypeField.id === 0 ? 'Create Field' : 'Edit Field'
    },
    typeText(): string {
      if (!this.inventoryTypeField) return ''

      const index = this.typeOptions.findIndex(t => t.id === this.inventoryTypeField.type)
      if (index === -1) return ''

      return this.typeOptions[index].name
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onAddOptionClicked(): void {
      let displayOrder = 1

      if (this.inventoryTypeFieldOptions.length > 0) {
        const currentMaxValue = this.inventoryTypeFieldOptions[this.inventoryTypeFieldOptions.length - 1].displayOrder
        displayOrder = currentMaxValue + 1
      }

      this.inventoryTypeFieldOptions.push(new InventoryTypeFieldOption(0, true, displayOrder, ''))
    },
    onCloseClicked(): void {
      this.$emit('close')
    },
    onDeleteClicked(inventoryTypeFieldOption: InventoryTypeFieldOption): void {
      inventoryTypeFieldOption.active = false
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onInventoryTypeFieldOptionDisplayOrderChanged(event: any): void {
      const oldIndex = event.moved.oldIndex

      let newIndex = event.moved.newIndex
      if (newIndex === this.inventoryTypeFieldOptions.length)
        newIndex -= 1;
      
      const offset = newIndex - oldIndex > 0 ? -1 : 1
      const positionsToMove = Math.abs(newIndex - oldIndex)
      for (let i = 0; i < positionsToMove; i++) {
        const swapOrder = this.inventoryTypeFieldOptions[newIndex].displayOrder;
        this.inventoryTypeFieldOptions[newIndex].displayOrder = this.inventoryTypeFieldOptions[newIndex + offset].displayOrder;
        this.inventoryTypeFieldOptions[newIndex + offset].displayOrder = swapOrder;
        newIndex += offset
      }
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const inventoryTypeFieldOptionMessages = this.inventoryTypeFieldOptions.map((inventoryTypeFieldOption: InventoryTypeFieldOption) => new InventoryTypeFieldOptionMessage(inventoryTypeFieldOption.id, inventoryTypeFieldOption.active, inventoryTypeFieldOption.displayOrder, String(inventoryTypeFieldOption.value)))
      if (this.inventoryTypeField.id === 0) {
        const createInventoryTypeFieldMessage = new CreateInventoryTypeFieldMessage(this.name, this.displayOrder, this.type, this.isRequired, inventoryTypeFieldOptionMessages)
        this.inventoryTypeService.createInventoryTypeField(this.inventoryTypeId, createInventoryTypeFieldMessage).then(() => {
          this.$emit('inventory-type-field-changed')
          this.toast.success('Field created successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the field. Please try again.')
          }
        })
      }
      else {
        const updateInventoryTypeFieldMessage = new UpdateInventoryTypeFieldMessage(this.name, this.displayOrder, this.isRequired, inventoryTypeFieldOptionMessages)
        this.inventoryTypeFieldService.updateInventoryTypeField(this.inventoryTypeField.id, updateInventoryTypeFieldMessage).then(() => {
          this.$emit('inventory-type-field-changed')
          this.toast.success('Field updated successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the field. Please try again.')
          }
        })
      }
    }
  }
})
